import React, { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { useMutation } from '@apollo/client';
import {
    Field, Form, Formik, FormikProps,
} from 'formik';
import { Label } from 'flowbite-react';

import { handleValidationErrors, onCompletedHandler, onErrorHandler } from '@/services/apollo';
import { initializedAtom, userAtom } from '@/components/with_auth/State';
import { UPDATE_BRIEF, UPDATE_USER } from '@/components/onboarding/Actions';
import { haveErrors, reportGeneralError, BIO_STATES } from '@/components/onboarding/Shared';
import PanelContainer from '@/components/commons/panel_container';
import routes from '@/services/routes';
import { type bioPageProps } from '@/types/globals';
import useIsEdit from '@/hooks/use_is_edit';
import FormError from '@/components/forms/FormError';

import bioStyles from './Bio.module.css';
import TextAreaInput from './TextArea';
import BioPageHeader from './BioPageHeader';
import NavButtons from './NavigationButtons';

interface Props extends bioPageProps {
    readonly header: string;
    readonly subHeader: string;
    readonly subHeaderWidth: string;
    readonly fieldName: string;
    readonly inputLabelText: string;
    readonly inputPlaceholder: string;
    readonly bioStep: number;
    readonly bioState: number;
    readonly totalSteps?: number;
    readonly stepNames?: object;
}

function PreferencePage({
    fieldName,
    header,
    subHeader,
    subHeaderWidth,
    inputLabelText,
    inputPlaceholder,
    bioStep,
    totalSteps = 3,
    bioState,
    stepNames = null,
    isEdit,
    isModal = false,
    openBrief,
    extendContinue = null,
    overrideBack = null,
}: Props) {
    const formRef = useRef<FormikProps<any>>(null);
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);

    const user = useRecoilValue(userAtom);
    const { url } = useIsEdit();

    const setters = {
        setUser: useSetRecoilState(userAtom),
        setInitialized: useSetRecoilState(initializedAtom),
    };

    const [updateUser] = useMutation(UPDATE_USER);
    const [updateBrief] = useMutation(UPDATE_BRIEF);

    const handleContinue = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const handleBackNavigation = () => {
        if (isModal && overrideBack) {
            overrideBack(false);
        } else if (bioState === 7) {
            router.push(routes.BIO_EDIT_BIO);
        } else {
            router.push(routes.BIO_COMMUNICATION_PREFERENCES);
        }
    };

    const handleUserUpdate = async (attributes, setSubmitting, setStatus) => {
        setIsLoading(true);
        updateUser({
            variables: {
                updateUserInput: {
                    attributes,
                },
            },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'no-cache',
            onError: onErrorHandler((error) => {
                setSubmitting(false);
                reportGeneralError(error);
                setIsLoading(false);
            }),
            onCompleted: onCompletedHandler(() => { }, async (data) => {
                setSubmitting(false);
                const { errors } = data.updateUser;
                if (haveErrors(errors)) {
                    handleValidationErrors(errors, setStatus, 'user');
                    return;
                }

                setters.setUser(data.updateUser.user);
                setters.setInitialized(true);
                setStatus({ errors: {} });

                setIsLoading(false);
            }),
        });
    };

    const handleBriefUpdate = async (attributes, setSubmitting, setStatus) => {
        setIsLoading(true);
        updateBrief({
            variables: {
                input: {
                    id: openBrief ? openBrief.briefIndex : user.briefs[user.briefs.length - 1].id,
                    attributes,
                },
            },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'no-cache',
            onError: onErrorHandler((error) => {
                setSubmitting(false);
                reportGeneralError(error);
            }),
            onCompleted: onCompletedHandler(() => { }, async (data) => {
                setSubmitting(false);
                const { errors } = data.updateBrief;
                if (haveErrors(errors)) {
                    handleValidationErrors(errors, setStatus, 'brief');
                    return;
                }
                setStatus({ errors: {} });
                setIsLoading(false);
                if (url) {
                    router.push(url);
                } else if (isModal && extendContinue) {
                    extendContinue('', isEdit);
                } else if (bioState === 7) {
                    router.push(routes.BIO_MEETING_PREFERENCES);
                } else {
                    router.push(routes.BIO_SOCIAL_LINKS);
                }
            }),
        });
    };

    const brief = openBrief
        ? user.briefs.find((item) => item.id === openBrief.briefIndex)
        : user.briefs[0];

    return (
        <PanelContainer paddingOverride="p-0 sm:p-8" step={bioStep} totalSteps={totalSteps} stepNames={stepNames} isModal={isModal}>
            <div className="w-full py-2 px-2 sm:px-5">
                <BioPageHeader
                    header={header}
                    subHeader={subHeader}
                    subHeaderWidth={subHeaderWidth}
                    subHeaderPadding="pb-4"
                />
                <Formik
                    innerRef={formRef}
                    onSubmit={async (frm, { setStatus, setSubmitting }) => {
                        const attributes = {
                            // onboardingState: bioState === 7
                            //     ? BIO_STATES.meetingPreferences : BIO_STATES.socialLinks,
                            [fieldName]: frm[fieldName]?.trim(),
                        };
                        await handleBriefUpdate(attributes, setSubmitting, setStatus);
                        await handleUserUpdate({
                            onboardingState: bioState === 7
                                ? BIO_STATES.meetingPreferences : BIO_STATES.socialLinks,
                        }, setSubmitting, setStatus);
                    }}
                    initialValues={{
                        [fieldName]: brief[fieldName] || '',
                    }}
                >
                    {({ values, status }) => (
                        <Form>
                            <div className="rounded-lg border border-gray-200 bg-cirrusWhite shadow-md p-4 sm:p-8 max-w-[1040px]">
                                <div className="flex flex-col gap-2">
                                    <div className="flex flex-col md:flex-row gap-2">
                                        <Label
                                            className="text-cleanSlate font-instrument text-base font-medium leading-7"
                                            value={inputLabelText}
                                        />
                                        <span className="font-instrument font-medium leading-18 text-cleanSlate flex items-center px-1.5 bg-gray-100 rounded-md">Optional</span>
                                    </div>
                                    <Field
                                        className="rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-gray-500 font-instrument text-base font-normal leading-7 min-h-[290px] resize-none"
                                        name={fieldName}
                                        id={fieldName}
                                        value={values[fieldName] || ''}
                                        placeholder={inputPlaceholder}
                                        component={TextAreaInput}
                                        required={false}
                                    />
                                    <FormError error={status?.errors?.[fieldName]} />
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
                <div className="w-full px-0 sm:px-4">
                    <NavButtons
                        containerStyles={bioStyles.buttonGroupWithoutBio}
                        handleBackNavigation={handleBackNavigation}
                        handleContinue={handleContinue}
                        isLoading={isLoading}
                        isEdit={url || isEdit}
                    />
                </div>
            </div>

        </PanelContainer>
    );
}

export default PreferencePage;
